import type { PremadeTemplate } from "@/lib/premade-contract-templates";

export const CLEANING_PROPOSAL_TEMPLATES = [
  "Office Cleaning Proposal",
  "Medical Facility Sanitation",
  "Post-Construction Cleanup",
  "Floor Care & Maintenance",
  "Window Cleaning Services",
];

export const CLEANING_CONTRACT_TEMPLATES: PremadeTemplate[] = [
  {
    name: "Standard Janitorial Service Agreement",
    description: "Recurring commercial cleaning contract covering scope of services, cleaning schedule, supplies, safety compliance, and quality assurance.",
    content: `COMMERCIAL JANITORIAL SERVICE AGREEMENT

This Janitorial Service Agreement ("Agreement") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Provider")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. SCOPE OF SERVICES

The Provider agrees to furnish commercial janitorial and cleaning services at the Client's facility, including but not limited to:

• General office cleaning (dusting, vacuuming, mopping)
• Restroom sanitation and restocking of supplies
• Trash and recycling removal
• High-touch surface disinfecting (door handles, light switches, elevator buttons)
• Kitchen/break room cleaning and sanitation
• Glass and mirror cleaning (interior)
• Floor care and maintenance

{{scope_of_work}}

Facility Description:
{{project_description}}

──────────────────────────────────────────────────

2. CLEANING SCHEDULE & FREQUENCY

The Provider shall perform cleaning services according to the following schedule:

{{estimated_hours}}

All cleaning shall be performed during agreed-upon hours. The Provider will coordinate with the Client regarding access requirements, alarm codes, and key control procedures.

──────────────────────────────────────────────────

3. TERM AND DURATION

{{contract_length}}

This Agreement shall commence on the date of execution and remain in effect for the duration specified above, unless terminated earlier in accordance with the terms herein. Either party may request renewal or extension prior to expiration.

──────────────────────────────────────────────────

4. COMPENSATION

Estimated Monthly Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Late payments shall incur a fee of 1.5% per month on the outstanding balance. The Provider reserves the right to suspend services if payment is not received within fifteen (15) days past the due date.

──────────────────────────────────────────────────

5. SUPPLIES & EQUIPMENT

A. The Provider shall furnish all necessary cleaning supplies, chemicals, and equipment unless otherwise agreed.
B. All cleaning products shall meet EPA standards and be appropriate for the facility type.
C. Material Safety Data Sheets (MSDS/SDS) for all chemicals used shall be maintained on-site and available upon request.
D. The Client shall provide adequate water, electricity, and waste disposal access.

──────────────────────────────────────────────────

6. SAFETY & COMPLIANCE

A. The Provider shall comply with all applicable OSHA regulations regarding chemical handling, personal protective equipment (PPE), and workplace safety.
B. All cleaning personnel shall be trained in proper chemical handling, bloodborne pathogen protocols, and slip/fall prevention.
C. The Provider shall maintain current workers' compensation insurance as required by law.
D. For healthcare facilities: The Provider shall comply with all applicable HIPAA, infection control, and biohazard disposal requirements.

──────────────────────────────────────────────────

7. ACCESS REQUIREMENTS

A. The Client shall provide the Provider with necessary keys, access cards, or alarm codes required to perform services.
B. All access credentials shall be kept secure and used solely for the purpose of performing contracted services.
C. The Provider shall ensure all entry points are secured upon completion of each cleaning session.
D. Lost or compromised credentials shall be reported to the Client immediately.

──────────────────────────────────────────────────

8. QUALITY ASSURANCE & INSPECTIONS

A. The Provider shall conduct periodic quality inspections to ensure service standards are maintained.
B. The Client may request a walk-through inspection at any time with reasonable notice.
C. Any service deficiencies reported by the Client shall be corrected within 24 hours of notification.
D. The Provider shall maintain cleaning logs documenting completed tasks, dates, and personnel.

──────────────────────────────────────────────────

9. CANCELLATION & TERMINATION

Either Party may terminate this Agreement with thirty (30) days' written notice. In the event of material breach, the non-breaching Party may terminate immediately with written notice. Upon termination, the Client shall pay for all services rendered through the termination date.

──────────────────────────────────────────────────

10. LIABILITY & INSURANCE

A. The Provider shall maintain commercial general liability insurance with minimum coverage of $1,000,000 per occurrence.
B. The Provider shall provide a certificate of insurance upon request.
C. The Provider shall be liable for any damage to Client property caused by Provider's negligence during the performance of services.
D. The Provider shall not be liable for pre-existing damage or normal wear and tear.

──────────────────────────────────────────────────

11. CONFIDENTIALITY

Both Parties agree to maintain confidentiality of all proprietary and sensitive information exchanged during this Agreement. This obligation survives termination for two (2) years.

──────────────────────────────────────────────────

12. ADDITIONAL TERMS

{{terms_and_conditions}}

Additional Notes:
{{proposal_notes}}

──────────────────────────────────────────────────

13. SIGNATURES

By signing below, the Parties acknowledge that they have read, understood, and agree to be bound by this Agreement.

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Deep Clean / One-Time Service Agreement",
    description: "Contract for one-time deep cleaning, post-construction cleanup, or move-in/move-out cleaning services.",
    content: `ONE-TIME DEEP CLEANING SERVICE AGREEMENT

This Deep Cleaning Service Agreement ("Agreement") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}

──────────────────────────────────────────────────

1. SERVICE DESCRIPTION

The Provider shall perform a comprehensive deep cleaning of the Client's facility, including:

• Detailed floor care: stripping, waxing, buffing, carpet extraction
• Wall and baseboard washing
• Deep restroom sanitation including grout cleaning
• Window cleaning (interior and exterior where accessible)
• High-dust areas: ceiling vents, light fixtures, top of cabinets
• Appliance cleaning (interior and exterior)
• Disinfecting all high-touch surfaces
• Trash removal and disposal

{{scope_of_work}}

{{project_description}}

──────────────────────────────────────────────────

2. SCHEDULE

Estimated Service Date(s): {{estimated_hours}}
Estimated Duration: As needed to complete the scope of work.

──────────────────────────────────────────────────

3. COMPENSATION

Total Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

A 50% deposit is due upon signing. The remaining balance is due upon satisfactory completion of services.

──────────────────────────────────────────────────

4. SUPPLIES & EQUIPMENT

The Provider shall furnish all necessary cleaning supplies, chemicals, and specialized equipment. Any hazardous materials encountered (e.g., biohazards, asbestos) are excluded from this scope and will require separate arrangements.

──────────────────────────────────────────────────

5. LIABILITY & INSURANCE

The Provider shall maintain commercial general liability insurance with minimum coverage of $1,000,000 per occurrence. The Client shall disclose any known hazards or sensitive areas prior to the service date.

──────────────────────────────────────────────────

6. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

7. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Specialized Sanitation Contract (Medical/HIPAA)",
    description: "Contract for healthcare facility cleaning with HIPAA compliance, infection control, and biohazard handling provisions.",
    content: `SPECIALIZED SANITATION SERVICE AGREEMENT — HEALTHCARE FACILITY

This Specialized Sanitation Agreement ("Agreement") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}

──────────────────────────────────────────────────

1. PURPOSE

The Provider shall furnish specialized sanitation and janitorial services for the Client's healthcare facility in full compliance with applicable HIPAA regulations, CDC guidelines, and OSHA standards.

──────────────────────────────────────────────────

2. SCOPE OF SERVICES

• Terminal cleaning and daily disinfection of patient areas
• Restroom sanitation with hospital-grade disinfectants
• Biohazard waste handling and disposal per regulatory standards
• High-touch surface disinfecting (minimum 3x daily)
• Floor care using non-slip, healthcare-approved products
• Waiting room and reception area maintenance
• Laundry room and utility area cleaning
• Operating room and exam room turnover cleaning

{{scope_of_work}}

{{project_description}}

──────────────────────────────────────────────────

3. COMPLIANCE REQUIREMENTS

A. All personnel shall complete HIPAA training and sign confidentiality agreements.
B. All personnel shall be trained in bloodborne pathogen standards (29 CFR 1910.1030).
C. The Provider shall use only EPA-registered, hospital-grade disinfectants.
D. Cleaning logs shall be maintained for regulatory audits.
E. The Provider shall participate in infection control meetings as requested.

──────────────────────────────────────────────────

4. SCHEDULE

{{estimated_hours}}

──────────────────────────────────────────────────

5. COMPENSATION

Estimated Monthly Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Healthcare sanitation services carry a premium rate reflecting specialized training, compliance, and chemical requirements.

──────────────────────────────────────────────────

6. INSURANCE & LIABILITY

A. The Provider shall maintain:
   • Commercial General Liability: $2,000,000 per occurrence
   • Professional Liability: $1,000,000
   • Workers' Compensation as required by law
B. The Provider shall indemnify the Client against claims arising from the Provider's negligence.

──────────────────────────────────────────────────

7. CONFIDENTIALITY

The Provider acknowledges that its personnel may be exposed to Protected Health Information (PHI) during the course of services. The Provider agrees to comply with all HIPAA privacy and security requirements and shall execute a Business Associate Agreement (BAA) if required.

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
];

export const CLEANING_PRICING_MODELS = {
  perSqFt: {
    label: "Per Square Foot",
    description: "Base rate per square foot with frequency and traffic multipliers",
  },
};

export const CLEANING_TERMINOLOGY = {
  proposalLabel: "Cleaning Proposal",
  contractLabel: "Service Agreement",
  staffLabel: "Cleaning Crew",
  rateLabel: "Per Square Foot Rate",
  industryTerms: [
    "janitorial services",
    "disinfecting",
    "high-touch surfaces",
    "restroom sanitation",
    "day porter",
    "nightly cleaning",
    "carpet extraction",
    "floor care",
  ],
};
